/* Field park: the supporting files sit in the field anatomy until the upload round's file field. */
import { datadogLogs } from '@datadog/browser-logs';
import { Field } from '@main/complykit/components/field';
import { FileFieldContext } from '@main/dynamic-form-fields';
import { Vendor_Questionnaire_Form_Answer_Statuses_Enum } from '@main/graphql/types.generated';
import { toError } from '@main/shared/utils';
import {
  errorToast,
  FileUpload,
  UploadedFile,
  useDownloadStorageFile,
  useEagerMultipleFilesUpload,
} from '@main/ui';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QuestionnaireFormFieldConfig,
  QuestionnnaireFieldWrapperProps,
  QuestionnnaireFormFieldAnswer,
  QuestionnnaireFormMode,
} from './form-types';

export interface QuestionnaireFieldFilesProps extends QuestionnnaireFieldWrapperProps {
  config: QuestionnaireFormFieldConfig;
  answer: QuestionnnaireFormFieldAnswer;
}

export function QuestionnaireFieldFiles(props: QuestionnaireFieldFilesProps) {
  const { t } = useTranslation();
  const {
    answerSupportFiles,
    questionSupportingFile: _questionSupportingFile,
    ...filesProps
  } = useContext(FileFieldContext);

  const isAnswerMode = props.form.mode === QuestionnnaireFormMode.Answer;
  const isCompleted =
    props.answer.status === Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed;
  const isReadOnly = isCompleted || !isAnswerMode;

  const supportingFilesUpload = useEagerMultipleFilesUpload({
    files: props.answer.support_files
      ?.filter((upload) => !!upload.file?.id)
      .map((upload) => upload.file as UploadedFile),
    onFileDownload: useDownloadStorageFile(),
    onFilesUpload: isAnswerMode
      ? async ({ errors, files }) => {
          if (errors.length) {
            errorToast(
              t('errorMessages.uploadFailed', {
                entity: t('entities.vendorQuestionnaireSupportingFile'),
              }),
            );
            for (const error of errors) {
              datadogLogs.logger.error(
                'Failed uploading supporting files for vendor questionnaire answer',
                { answerId: props.answer.id },
                toError(error),
              );
            }
            return;
          }

          const support_files = [
            ...(props.answer.support_files ?? []),
            ...files.map((file) => ({ file_id: file.id })),
          ];
          const answerData: Partial<QuestionnnaireFormFieldAnswer> = {
            support_files,
          };

          await props.onAnswerChange?.(props.answer, answerData);
        }
      : undefined,
    onFileDelete: isAnswerMode
      ? async (fileId) => {
          const support_files = props.answer.support_files?.filter(
            (upload) => upload.file?.id !== fileId,
          );
          const answerData: Partial<QuestionnnaireFormFieldAnswer> = {
            support_files,
          };

          await props.onAnswerChange?.(props.answer, answerData);
        }
      : undefined,
  });

  return (
    <Field label={t('questionnaires.form.supportingFiles')} isReadOnly={isReadOnly}>
      {isReadOnly ? (
        <FileUpload {...supportingFilesUpload} {...filesProps} {...answerSupportFiles} isReadOnly>
          <FileUpload.Files />
        </FileUpload>
      ) : (
        (fieldControl) => (
          <FileUpload {...supportingFilesUpload} {...filesProps} {...answerSupportFiles}>
            <FileUpload.Files />
            <FileUpload.Dropzone inputProps={fieldControl} />
          </FileUpload>
        )
      )}
    </Field>
  );
}
